import { isSiheungVerticalMyLocationEnabled, LEGACY_MY_LOCATION_MARKER, MY_LOCATION_ACTIVE_ID, MY_LOCATION_ANIMATE, MY_LOCATION_MARKER, MY_LOCATION_ZOOM_UPDATE_DEBOUNCE_MS } from '@/constants/floor-guide';
import { useCalculateIconSize } from '@/hooks/floor-guide';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores';
import { useMarkerStore } from '@/stores/markerStore';
import {
    calculateMyLocationPulseIconSize,
    clearMyLocationFromMap,
    mapMyLocationPinMarker,
    mapMyLocationPulseMyLocOption,
    mapMyLocationToMarker,
} from '@/utils/marker';
import { calculateZoomAwareScreenIconSize } from '@/utils/marker-size';
import { safeMoveToMarker, waitForMapRender } from '@/utils/map';
import type { DabeeoMap } from 'dabeeomaps';
import { useCallback, useRef } from 'react';

export const useMyCurrentLocation = (currentMap?: DabeeoMap) => {
    const server = useConfigStore((state) => state.config?.server);
    const storeCode = useConfigStore((state) => state.config?.server?.store?.storeCode) ?? '';
    const { isHorizontal } = useOrientation();
    const isSiheungVerticalMyLocationDisplay = isSiheungVerticalMyLocationEnabled(storeCode, isHorizontal);
    const { setMarkerIds } = useMarkerStore();
    const { calculateIconSize, calculateUniformIconSize } = useCalculateIconSize();
    const moveToTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pulseCleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const zoomUpdateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingZoomRef = useRef<number | null>(null);
    const zoomUpdateVersionRef = useRef(0);

    const buildMyLocationIconSize = useCallback(
        (zoom: number) => {
            const baseScreenSize = calculateUniformIconSize(MY_LOCATION_MARKER.BASE_SIZE);
            return calculateZoomAwareScreenIconSize({
                screenIconSize: baseScreenSize,
                referenceZoom: MY_LOCATION_MARKER.REFERENCE_ZOOM,
                currentZoom: zoom,
                minVisiblePx: MY_LOCATION_MARKER.MIN_VISIBLE_PX,
            });
        },
        [calculateUniformIconSize]
    );

    const buildLegacyMyLocationIconSize = useCallback(
        (zoom: number) => {
            const baseScreenSize = calculateIconSize(LEGACY_MY_LOCATION_MARKER.BASE_SIZE);
            return calculateZoomAwareScreenIconSize({
                screenIconSize: baseScreenSize,
                referenceZoom: LEGACY_MY_LOCATION_MARKER.REFERENCE_ZOOM,
                currentZoom: zoom,
                minVisiblePx: LEGACY_MY_LOCATION_MARKER.MIN_VISIBLE_PX,
            });
        },
        [calculateIconSize]
    );

    const buildMyLocationPulseIconSize = useCallback(
        (zoom: number) => {
            const basePulseScreenSize = calculateUniformIconSize({
                width: MY_LOCATION_MARKER.PULSE_SIZE,
                height: MY_LOCATION_MARKER.PULSE_SIZE,
            });
            const pulseScreenSize = calculateZoomAwareScreenIconSize({
                screenIconSize: basePulseScreenSize,
                referenceZoom: MY_LOCATION_MARKER.REFERENCE_ZOOM,
                currentZoom: zoom,
                minVisiblePx: MY_LOCATION_MARKER.MIN_VISIBLE_PX,
            });
            return calculateMyLocationPulseIconSize(pulseScreenSize);
        },
        [calculateUniformIconSize]
    );

    const setMyLocationPinMarker = useCallback(
        async (zoom: number): Promise<string | undefined> => {
            if (!currentMap || !server?.floor?.floorCode || !server.machine) {
                return undefined;
            }
            if (isSiheungVerticalMyLocationDisplay) {
                const iconSize = buildMyLocationIconSize(zoom);
                const pinMarker = mapMyLocationPinMarker(server.floor.floorCode, server.machine, iconSize);
                const pinMarkerIds = await currentMap.markers?.set?.({ marker: [pinMarker] });
                return Array.isArray(pinMarkerIds) ? pinMarkerIds[0] : undefined;
            }
            const iconSize = buildLegacyMyLocationIconSize(zoom);
            const legacyMarker = mapMyLocationToMarker(server.floor.floorCode, server.machine, iconSize);
            if (!legacyMarker) {
                return undefined;
            }
            const markerIds = await currentMap.markers?.set?.({ marker: [legacyMarker] });
            return Array.isArray(markerIds) ? markerIds[0] : undefined;
        },
        [
            buildLegacyMyLocationIconSize,
            buildMyLocationIconSize,
            currentMap,
            isSiheungVerticalMyLocationDisplay,
            server?.floor?.floorCode,
            server?.machine,
        ]
    );

    const applyMyLocationPulse = useCallback(
        async (zoom: number) => {
            if (!isSiheungVerticalMyLocationDisplay || !currentMap || !server?.floor?.floorCode || !server.machine) {
                return;
            }
            const pulseIconSize = buildMyLocationPulseIconSize(zoom);
            const pulseOption = mapMyLocationPulseMyLocOption(server.floor.floorCode, server.machine, pulseIconSize);
            await currentMap.mylocation?.set?.(pulseOption);
            setMarkerIds({ myLocationPulse: MY_LOCATION_ACTIVE_ID });
            if (pulseCleanupTimeoutRef.current) {
                clearTimeout(pulseCleanupTimeoutRef.current);
            }
            pulseCleanupTimeoutRef.current = setTimeout(() => {
                currentMap.mylocation?.clear?.();
                setMarkerIds({ myLocationPulse: undefined });
                pulseCleanupTimeoutRef.current = null;
            }, MY_LOCATION_ANIMATE.DURATION * MY_LOCATION_ANIMATE.REPEAT + 100);
        },
        [
            buildMyLocationPulseIconSize,
            currentMap,
            isSiheungVerticalMyLocationDisplay,
            server?.floor?.floorCode,
            server?.machine,
            setMarkerIds,
        ]
    );

    const clearMyCurrentLocation = useCallback(() => {
        if (moveToTimeoutRef.current) {
            clearTimeout(moveToTimeoutRef.current);
            moveToTimeoutRef.current = null;
        }
        if (pulseCleanupTimeoutRef.current) {
            clearTimeout(pulseCleanupTimeoutRef.current);
            pulseCleanupTimeoutRef.current = null;
        }
        if (zoomUpdateDebounceRef.current) {
            clearTimeout(zoomUpdateDebounceRef.current);
            zoomUpdateDebounceRef.current = null;
        }
        pendingZoomRef.current = null;
        clearMyLocationFromMap(currentMap);
    }, [currentMap]);

    const applyMyLocationMarkerForZoom = useCallback(
        async (zoom: number, version: number): Promise<void> => {
            if (!currentMap) return;
            const { myLocation, myLocationPulse } = useMarkerStore.getState().markerIds;
            if (!myLocation) return;
            if (!server?.floor?.floorCode || !server.machine) return;
            currentMap.markers?.clear?.([myLocation]);
            const nextPinMarkerId = await setMyLocationPinMarker(zoom);
            if (version !== zoomUpdateVersionRef.current) return;
            setMarkerIds({ myLocation: nextPinMarkerId });
            if (!isSiheungVerticalMyLocationDisplay || !myLocationPulse) return;
            const pulseIconSize = buildMyLocationPulseIconSize(zoom);
            const pulseOption = mapMyLocationPulseMyLocOption(server.floor.floorCode, server.machine, pulseIconSize);
            await currentMap.mylocation?.set?.(pulseOption);
        },
        [
            buildMyLocationPulseIconSize,
            currentMap,
            isSiheungVerticalMyLocationDisplay,
            server?.floor?.floorCode,
            server?.machine,
            setMarkerIds,
            setMyLocationPinMarker,
        ]
    );

    const updateMyLocationMarkerForZoom = useCallback(
        (zoom: number) => {
            pendingZoomRef.current = zoom;
            if (zoomUpdateDebounceRef.current) {
                clearTimeout(zoomUpdateDebounceRef.current);
            }
            zoomUpdateDebounceRef.current = setTimeout(() => {
                zoomUpdateDebounceRef.current = null;
                const pendingZoom = pendingZoomRef.current;
                pendingZoomRef.current = null;
                if (pendingZoom === null) return;
                zoomUpdateVersionRef.current += 1;
                const version = zoomUpdateVersionRef.current;
                void applyMyLocationMarkerForZoom(pendingZoom, version);
            }, MY_LOCATION_ZOOM_UPDATE_DEBOUNCE_MS);
        },
        [applyMyLocationMarkerForZoom]
    );

    const replayMyLocationPulse = useCallback(async (): Promise<void> => {
        if (!currentMap || !server?.floor?.floorCode || !server.machine) {
            return;
        }
        const { myLocation } = useMarkerStore.getState().markerIds;
        if (!myLocation) {
            return;
        }
        if (!isSiheungVerticalMyLocationDisplay) {
            return;
        }
        currentMap.mylocation?.clear?.();
        const currentZoom = currentMap.control?.getCurrentCameraInfo?.()?.zoom ?? server.machine.zoom ?? MY_LOCATION_MARKER.REFERENCE_ZOOM;
        await applyMyLocationPulse(currentZoom);
    }, [applyMyLocationPulse, currentMap, isSiheungVerticalMyLocationDisplay, server?.floor?.floorCode, server?.machine]);

    const addMyCurrentLocation = useCallback(
        async (withPulse: boolean = true) => {
            if (!currentMap) return;
            if (moveToTimeoutRef.current) {
                clearTimeout(moveToTimeoutRef.current);
                moveToTimeoutRef.current = null;
            }
            clearMyCurrentLocation();
            const currentZoom = server?.machine?.zoom ?? MY_LOCATION_MARKER.REFERENCE_ZOOM;
            const pinMarkerId = await setMyLocationPinMarker(currentZoom);
            if (!pinMarkerId) return;
            setMarkerIds({ myLocation: pinMarkerId });
            if (withPulse && isSiheungVerticalMyLocationDisplay) {
                await applyMyLocationPulse(currentZoom);
            }
            currentMap?.control?.changeZoom?.({ transition: true, zoom: server?.machine?.zoom ?? 0 });
            await waitForMapRender(3);
            await safeMoveToMarker(
                currentMap,
                { x: server?.machine?.positionX ?? 0, y: server?.machine?.positionY ?? 0 },
                server?.floor?.floorCode,
                true,
                5,
                'addMyCurrentLocation'
            );
            const { poiList, selectedPoi } = useMarkerStore.getState().markerIds;
            currentMap?.markers?.clear?.([...(poiList ?? []), ...(selectedPoi ? [selectedPoi] : [])]);
            setMarkerIds({ poiList: [], selectedPoi: undefined });
        },
        [
            applyMyLocationPulse,
            clearMyCurrentLocation,
            currentMap,
            isSiheungVerticalMyLocationDisplay,
            server?.floor?.floorCode,
            server?.machine,
            setMarkerIds,
            setMyLocationPinMarker,
        ]
    );

    return {
        addMyCurrentLocation,
        clearMyCurrentLocation,
        replayMyLocationPulse,
        updateMyLocationMarkerForZoom,
    };
};
